import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { buildHead } from "@/lib/seo-head";
import { Section, SectionHeader } from "@/components/site/Blocks";
import { partnerBySlug } from "@/data/partners";
import { PartnerCTA, PartnerDisclosure } from "@/components/site/PartnerBlocks";
import { WhyYouNeedIt, HowItWorks } from "@/components/site/BusinessSetupBlocks";
import { PageEssentials, PageOutro } from "@/components/site/PageEssentials";
import heroBusinessLoans from "@/assets/hero/hero-business-loans.jpg.asset.json";

const HERO = {
  src: heroBusinessLoans.url,
  alt: "Real estate investor at a bright desk reviewing business credit statements with a growth chart on her laptop and a duplex rental property through the window — business loans and business credit under an LLC",
};

export const Route = createFileRoute("/business-setup/business-loans")({
  head: () =>
    buildHead({
      title: "Business Loans & Business Credit for Investors | Simply Approved",
      description:
        "Working capital and business lines that help real estate investors build business credit under their LLC, separate from personal credit.",
      url: "/business-setup/business-loans",
      type: "article",
      keywords:
        "business loans for real estate investors, business credit for rental property LLC, working capital for rehab carry, business line of credit investor, build business credit for LLC, business financing for fix and flip, business credit separate from personal credit, entity credit file real estate, rehab carry funding, business credit for portfolio investors, business loan for holding costs",
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "Business Setup", url: "/business-setup" },
        { name: "Business loans & credit", url: "/business-setup/business-loans" },
      ],
      service: { name: "Business Loans & Credit for Investors" },
      image: HERO.src,
      imageAlt: HERO.alt,
    }),
  component: BusinessLoansPage,
});

function BusinessLoansPage() {
  const partner = partnerBySlug("simply-approved-business-loans")!;
  return (
    <SiteShell>
      <PageHero
        eyebrow="Business Setup"
        title="Business loans that help build business credit"
        subtitle="A credit file under your LLC gives you capital for rehab carry, marketing, and operations without leaning on personal credit lines."
        image={HERO}
      />
      <PageEssentials />
      <WhyYouNeedIt
        service={"business credit and working capital"}
        intro={"Mortgage money buys the property; working capital runs the business around it. Rehab carry, holding costs, marketing, and contractor payments all hit before rents start, and paying for them with personal cards puts those balances on your personal credit exactly when you are applying for a mortgage."}
        reasons={[
          { title: "Keeps personal credit clean while you finance deals", body: "Carrying rehab costs on personal cards spikes utilization and can lower the credit score your loan pricing is based on." },
          { title: "Covers the gap before rent starts", body: "Between closing and the first rent check there are carrying costs. A line under the entity smooths that gap." },
          { title: "Builds a credit file under the LLC", body: "Business accounts that report on time give the entity a track record it can borrow against later." },
          { title: "Speed on the next opportunity", body: "Capital already in place means you can move on a deal without waiting for a new approval cycle." },
        ]}
      />
      <Section>
        <SectionHeader eyebrow="Why it matters" title="What a business credit profile does for an investor" />
        <ul className="mt-6 grid gap-3 text-sm md:grid-cols-2">
          {[
            "Keeps operating expenses off personal revolving credit, which supports your personal FICO",
            "Gives the entity its own payment history for future vendor and supplier terms",
            "Provides flexible capital for earnest money, rehab carry, and holding costs",
            "Creates cleaner books when underwriting reviews entity bank statements",
          ].map((i) => (
            <li key={i} className="rounded-lg border border-border bg-card p-4">{i}</li>
          ))}
        </ul>
        <HowItWorks
          steps={[
            "Get the entity, EIN, and business bank account in place first.",
            "Apply for a business line or term facility sized to your rehab and holding costs.",
            "Pay it on time from the business account so the history reports under the entity.",
          ]}
        />
        <PartnerCTA partner={partner} />
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/apply" className="rounded-md bg-brand-gold px-5 py-3 text-sm font-semibold text-primary-foreground">Start My Approval</Link>
          <Link to="/business-setup/duns-number" className="rounded-md border border-brand px-5 py-3 text-sm font-semibold text-brand">Get a DUNS number</Link>
        </div>
        <PartnerDisclosure />
      </Section>
    <PageOutro />
      </SiteShell>
  );
}
